import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'react-feather';

interface ProfilePicturePreviewProps {
  open: boolean;
  onClose: () => void;
  imageUrl: string;
  title?: string;
  alt?: string;
}

export const ProfilePicturePreview = ({
  open,
  onClose,
  imageUrl,
  title = 'Wyświetl zdjęcie profilowe',
  alt = 'Profile',
}: ProfilePicturePreviewProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const content = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ fontFamily: 'Roboto, sans-serif' }}
      onClick={handleOverlayClick}
      data-testid="profile-picture-preview-overlay"
    >
      <div className="absolute inset-0 bg-black/50" aria-hidden="true" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative bg-white rounded-[12px] p-6 flex flex-col items-center gap-6 shadow-xl"
        style={{
          width: 'min(100%, 436px)',
          height: 'min(100%, 342px)',
        }}
        data-testid="profile-picture-preview-dialog"
      >
        <div className="relative flex items-center justify-center w-full">
          <p
            className="text-[18px] leading-[26px] text-on-surface-variant font-normal text-center px-10"
            data-testid="profile-picture-preview-title"
          >
            {title}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#474A4C14] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-color-30 cursor-pointer"
            aria-label="Zamknij"
            data-testid="profile-picture-preview-close"
          >
            <X size={24} color="#474A4C" />
          </button>
        </div>

        <div className="w-[244px] h-[244px] max-w-full rounded-full overflow-hidden bg-[#E8EEF0] flex items-center justify-center">
          <img
            src={imageUrl}
            alt={alt}
            className="w-full h-full object-cover"
            data-testid="profile-picture-preview-image"
          />
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default ProfilePicturePreview;
